import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiListBold, PiXBold } from "react-icons/pi";
import LogoUploader from "./LogoUploader";
import LoginDropdown from "./LoginDropdown";

export default function CenteredHeader({
  content,
  onContentChange,
  isSelected,
  defaultLogo,
}: SectionContentProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const primaryColor = content.colorPalette?.primary || "#3B82F6";
  const secondaryColor = content.colorPalette?.secondary || "#4F46E5";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const padding = content.padding || "normal";
  const getPaddingClass = () => {
    if (padding === "compact") return "py-2";
    if (padding === "spacious") return "py-10";
    return "py-5";
  };

  const handleLogoChange = (newLogoUrl: string) => {
    if (onContentChange) {
      onContentChange({ ...content, logo: newLogoUrl });
    }
  };

  const renderLogo = () => (
    <LogoUploader
      currentLogo={content.logo}
      defaultLogo={defaultLogo}
      onLogoChange={handleLogoChange}
      heightClass="h-10"
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      companyName={content.companyName || "INSTACKS"}
      isEditing={!!onContentChange}
    />
  );

  const isEditing = !!onContentChange;

  const renderNavItems = (items: any[], offset: number = 0) =>
    items.map((item: any, idx: number) => {
      const navItem = typeof item === 'string' 
          ? { label: item, sectionId: item.toLowerCase().replace(/[^a-z0-9]+/g, '-') } 
          : item;
          
      const isCustomLink = navItem.sectionId === "custom";
      const targetHref = isCustomLink ? navItem.url : `#${navItem.sectionId}`;

      return (
        <a
          key={offset + idx}
          href={isEditing ? undefined : targetHref}
          target={isCustomLink && !isEditing ? "_blank" : undefined}
          rel={isCustomLink ? "noopener noreferrer" : undefined}
          onClick={(e) => {
            if (isEditing) {
              e.preventDefault();
            } else if (!isCustomLink) {
              e.preventDefault();
              const el = document.getElementById(navItem.sectionId);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }}
        >
          <EditableText
            value={navItem.label}
            onChange={(val) => {
              const newItems = [...(content.navItems || [])];
              newItems[offset + idx] = { ...navItem, label: val };
              if (onContentChange)
                onContentChange({ ...content, navItems: newItems });
            }}
            className="text-sm font-semibold transition-all cursor-pointer px-4 py-2 rounded-full uppercase tracking-wide border border-transparent hover:border-gray-200 hover:bg-gray-50 hover:shadow-sm"
            style={{
              color: primaryColor,
            }}
            as="span"
            placeholder="NAV ITEM"
          />
        </a>
      );
    });

  const navItems = content.navItems || [];
  const midPoint = Math.ceil(navItems.length / 2);
  const leftNav = navItems.slice(0, midPoint);
  const rightNav = navItems.slice(midPoint);

  return (
    <>
      <div
        className={`w-full transition-all overflow-hidden ${isSelected ? "ring-2 ring-blue-500 relative z-20" : "border-b border-gray-100"}`}
        style={{
          fontFamily,
          backgroundColor: content.backgroundColor || "#ffffff",
        }}
      >
        <div className={`max-w-7xl mx-auto px-8 ${getPaddingClass()}`}>
          <div className="flex items-center justify-between md:justify-center relative">
            <div className="md:hidden">
              <button
                className="p-2 -ml-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <PiListBold className="w-6 h-6" />
              </button>
            </div>

            <div className="hidden md:flex items-center gap-12 w-full justify-center">
              <nav className="flex items-center gap-8">
                {renderNavItems(leftNav, 0)}
              </nav>
              <div className="flex-shrink-0 mx-4 transform hover:scale-105 transition-transform">
                {renderLogo()}
              </div>
              <nav className="flex items-center gap-8">
                {renderNavItems(rightNav, midPoint)}
              </nav>
            </div>

            <div className="md:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <LogoUploader
                currentLogo={content.logo}
                defaultLogo={defaultLogo}
                onLogoChange={handleLogoChange}
                heightClass="h-8"
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                companyName={content.companyName || "INSTACKS"}
                isEditing={!!onContentChange}
              />
            </div>

            <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-4">
              <LoginDropdown variant="text" primaryColor={primaryColor} />
              {content.ctaText && (
                <button
                  className="rounded-full px-5 py-2 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                  }}
                >
                  <EditableText
                    value={content.ctaText}
                    onChange={(val) =>
                      onContentChange &&
                      onContentChange({ ...content, ctaText: val })
                    }
                    className="text-white"
                    as="span"
                    placeholder="CTA"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col md:hidden" style={{ fontFamily }}>
          <div className="flex items-center justify-between p-4 sm:p-8 border-b border-gray-100">
            <LogoUploader
              currentLogo={content.logo}
              defaultLogo={defaultLogo}
              onLogoChange={handleLogoChange}
              heightClass="h-8"
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              companyName={content.companyName || "INSTACKS"}
              isEditing={!!onContentChange}
            />
            <button
              className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <PiXBold className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
            <nav className="flex flex-col gap-4">
              {navItems.map((item: any, idx: number) => {
                const navItem = typeof item === 'string' 
                    ? { label: item, sectionId: item.toLowerCase().replace(/[^a-z0-9]+/g, '-') } 
                    : item;
                    
                const isCustomLink = navItem.sectionId === "custom";
                const targetHref = isCustomLink ? navItem.url : `#${navItem.sectionId}`;

                return (
                  <a
                    key={idx}
                    href={isEditing ? undefined : targetHref}
                    target={isCustomLink && !isEditing ? "_blank" : undefined}
                    rel={isCustomLink ? "noopener noreferrer" : undefined}
                    className="text-lg font-semibold border-b border-gray-50 pb-4"
                    style={{ color: primaryColor }}
                    onClick={(e) => {
                      if (isEditing) {
                        e.preventDefault();
                      } else if (!isCustomLink) {
                        e.preventDefault();
                        setIsMobileMenuOpen(false);
                        const el = document.getElementById(navItem.sectionId);
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth' });
                        }
                      } else {
                        setIsMobileMenuOpen(false);
                      }
                    }}
                  >
                    {navItem.label}
                  </a>
                );
              })}
            </nav>
            <div className="mt-auto pt-8 flex flex-col gap-4 border-t border-gray-100">
              <div className="w-full text-center py-2 border border-gray-200 rounded-lg font-semibold" style={{ color: primaryColor }}>
                <LoginDropdown variant="text" primaryColor={primaryColor} />
              </div>
              {content.ctaText && (
                <button
                  className="w-full rounded-lg px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {content.ctaText}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
